/**
 * SettingsPageLayout Component
 *
 * Reusable layout for all settings pages
 * Eliminates duplicate structure code across settings pages
 */

import React from "react";
import { cn } from "../utils/cn";
import { useSwipe } from "../hooks/useSwipe";
import { ProgressDots } from "./ProgressDots";
import { SETTINGS_CATEGORIES } from "../constants/settings";
import type { SettingsCategory } from "../types/settings";

export interface SettingsPageLayoutProps {
  /** Page title (e.g., "Profile", "Assets") */
  title: string;
  /** Settings category for navigation */
  category: SettingsCategory;
  /** Whether data is loading */
  loading?: boolean;
  /** Form content */
  children: React.ReactNode;
  /** Callback for back button - if provided, shows back button; if not provided, hides back button */
  onBack?: () => void;
  /** Callback when user completes flow (shows green checkmark FAB) */
  onDone?: () => void;
  /** Whether this is the last screen in onboarding */
  isLastScreen?: boolean;
  /** Whether to show the Done button (for onboarding, only show after all screens viewed) */
  showDone?: boolean;
  /** Whether to wrap around when swiping at edges (default: true) */
  wrapAround?: boolean;
  /** Base path for navigation (default: '/settings') */
  basePath?: string;
  /** Custom screens list for swipe navigation (default: SETTINGS_CATEGORIES) */
  screens?: readonly string[];
  /** Whether the user can complete onboarding (profile fields set) */
  canComplete?: boolean;
  /** List of missing required fields */
  missingFields?: string[];
}

/**
 * SettingsPageLayout - Common layout for all settings pages
 *
 * Features:
 * - Mobile/desktop responsive headers
 * - Progress indicator dots (mobile only)
 * - Swipe navigation support (mobile only)
 * - Loading state handling
 * - Optional back button (mobile only)
 * - Onboarding mode support with custom navigation
 *
 * @param props - Component props
 * @returns Settings page layout
 *
 * @example
 * ```tsx
 * function InvestableAssetsSettings() {
 *   const navigate = useNavigate();
 *   return (
 *     <SettingsPageLayout
 *       title="Investable Assets"
 *       category="investableAssets"
 *       loading={loading}
 *       onBack={() => navigate('/settings')}
 *     >
 *       <form>
 *         // Your form fields here
 *       </form>
 *     </SettingsPageLayout>
 *   );
 * }
 * ```
 */
export const SettingsPageLayout: React.FC<SettingsPageLayoutProps> = ({
  title,
  category,
  loading = false,
  children,
  onBack,
  onDone,
  isLastScreen = false,
  showDone,
  wrapAround = true,
  basePath = "/settings",
  screens,
  canComplete = true,
  missingFields = [],
}) => {
  const { swipeRef, handleTouchStart, handleTouchEnd } = useSwipe({
    current: category,
    screens: screens ?? SETTINGS_CATEGORIES,
    basePath,
    wrapAround,
  });

  // Show green checkmark FAB based on showDone prop if provided,
  // otherwise fall back to legacy behavior (onDone && isLastScreen)
  const showDoneFAB =
    showDone !== undefined ? showDone : onDone && isLastScreen;

  // Show loading state
  if (loading) {
    return (
      <div className="app-container" id={`settings-${category}`}>
        <div className="settings-page-container">
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="app-container"
      id={`settings-${category}`}
      ref={swipeRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <header className="settings-mobile-header">
        <div className="header-content">
          {onBack ? (
            <button
              onClick={onBack}
              className="back-btn"
              aria-label="Back to Settings"
            >
              <svg
                className="icon-size-medium"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          ) : (
            <div className="header-spacer" />
          )}
          <h1 className="settings-page-heading">{title}</h1>
          <div className="header-spacer" /> {/* Spacer for alignment */}
        </div>
        {/* Progress dots - mobile only */}
        <ProgressDots
          total={SETTINGS_CATEGORIES.length}
          current={SETTINGS_CATEGORIES.indexOf(category)}
        />
      </header>

      {/* Desktop header - only show in modal on desktop */}
      <header className="settings-desktop-header">
        <h1 className="settings-page-heading-large">{title}</h1>
      </header>

      {/* Main content area */}
      <main className="settings-main">{children}</main>

      {/* Done button footer - onboarding mode only, last screen only */}
      {showDoneFAB && (
        <div className="settings-done-bar">
          {/* Error message for missing fields */}
          {!canComplete && missingFields.length > 0 && (
            <p className="settings-error-text">
              Please set {missingFields.join(" and ")} in the Profile section to
              continue
            </p>
          )}

          <button
            onClick={onDone}
            disabled={!canComplete}
            className={cn("btn-done", !canComplete && "btn-done-disabled")}
            aria-label="Complete onboarding"
          >
            <span>Done</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SettingsPageLayout;
